import React from 'react';
import dayjs from 'dayjs';
import type { BillingStepKey } from './steps';
import { addressSummary, formatTodayStartDate, taxonomyDiffersFromNppes, taxonomyManualInvalid } from './utils';
import s from './s.module.less';

export interface ReviewSectionMeta {
    key: BillingStepKey;
    title: string;
    stepIndex: number;
}

// 每步一张 review 卡（标题与原型 reviewSections 一致；key = 后端 onboardingStep slug）
export const REVIEW_SECTIONS: ReviewSectionMeta[] = [
    { key: 'billing-provider', title: 'Billing provider', stepIndex: 0 },
    { key: 'tax-info', title: 'Tax information', stepIndex: 1 },
    { key: 'address', title: 'Billing address & contact', stepIndex: 2 },
    { key: 'service-location', title: 'Service location', stepIndex: 3 },
    { key: 'rendering-providers', title: 'Rendering providers', stepIndex: 4 },
    { key: 'claim-scope', title: 'EHR & billing scope', stepIndex: 5 },
    { key: 'era', title: 'Clearinghouse connection', stepIndex: 6 },
    { key: 'payment-method', title: 'Payment', stepIndex: 7 },
];

type Values = Record<string, any>;

export type NamePath = (string | number)[];

const isBlank = (v: any) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

const getByPath = (v: Values, name: NamePath | null) =>
    name == null ? undefined : name.reduce<any>((acc, k) => (acc == null ? acc : acc[k]), v);

// ---------- 格式校验辅助 ----------
const digits = (v: any) => String(v ?? '').replace(/\D/g, '');
const validNpi = (v: any) => /^\d{10}$/.test(digits(v));
// ZIP 存纯数字：5 位或 9 位（ZIP+4）
const validZip = (v: any) => /^\d{5}(\d{4})?$/.test(digits(v));
const validEmail = (v: any) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v ?? ''));
const validPhone = (v: any) => digits(v).length === 10;

interface InvalidField {
    name: NamePath;
    error: string;
    // NpiRegistryBox 已在该字段下方展示同义错误（红框/loading），Review 标红时跳过
    // 字段级红字避免重复；banner / Attention 徽标仍列出（getSectionErrors 不过滤）
    inlineShown?: boolean;
}

// present-but-invalid：有值但格式不合理，在 Review 处一并拦截标红。
// key 为后端 onboardingStep slug（kebab-case），豁免 camelCase 命名规则
/* eslint-disable @typescript-eslint/naming-convention */
const INVALID: Partial<Record<BillingStepKey, (v: Values) => InvalidField[]>> = {
    'billing-provider': (v) => {
        const out: InvalidField[] = [];
        const b = v.billing || {};
        if (!isBlank(b.npi) && !validNpi(b.npi))
            out.push({ name: ['billing', 'npi'], error: 'NPI must be 10 digits', inlineShown: true });
        if (b.npiType === 'notfound')
            out.push({ name: ['billing', 'npi'], error: 'NPI not found in the NPI Registry', inlineShown: true });
        // 填了就必须查到：查询中或无查询结果（如 hydrate 后从未触发查询）都拦；
        // 标红展开 section 后 NpiRegistryBox 挂载会自动补查，状态可自愈
        else if (b.npiChecking)
            out.push({
                name: ['billing', 'npi'],
                error: 'NPI is being verified, please wait a moment',
                inlineShown: true,
            });
        else if (!isBlank(b.npi) && validNpi(b.npi) && !b.npiType)
            out.push({ name: ['billing', 'npi'], error: 'NPI could not be verified in the NPI Registry' });
        // 手填 taxonomy 格式不对：字段下方已有红字，这里只负责拦截提交
        if (taxonomyManualInvalid(b))
            out.push({ name: ['billing', 'taxonomy'], error: 'Taxonomy code is not valid', inlineShown: true });
        return out;
    },
    'tax-info': (v) => {
        const out: InvalidField[] = [];
        const t = v.tax || {};
        if (!isBlank(t.taxId) && digits(t.taxId).length !== 9) {
            out.push({
                name: ['tax', 'taxId'],
                error:
                    t.taxIdType === 'ssn' ? 'SSN must be 9 digits (XXX-XX-XXXX)' : 'EIN must be 9 digits (XX-XXXXXXX)',
            });
        }
        return out;
    },
    address: (v) => {
        const out: InvalidField[] = [];
        const a = v.address || {};
        if (!isBlank(a.postalCode) && !validZip(a.postalCode))
            out.push({ name: ['address', 'postalCode'], error: 'ZIP must be 5 or 9 digits' });
        if (!isBlank(a.phone) && !validPhone(a.phone))
            out.push({ name: ['address', 'phone'], error: 'Phone must be 10 digits' });
        if (!isBlank(a.fax) && !validPhone(a.fax))
            out.push({ name: ['address', 'fax'], error: 'Fax must be 10 digits' });
        if (!isBlank(a.contactEmail) && !validEmail(a.contactEmail))
            out.push({ name: ['address', 'contactEmail'], error: 'Enter a valid email' });
        return out;
    },
    'service-location': (v) => {
        const out: InvalidField[] = [];
        const l = v.location || {};
        if (l.sameAsBilling === 'no' && !isBlank(l.postalCode) && !validZip(l.postalCode))
            out.push({ name: ['location', 'postalCode'], error: 'ZIP must be 5 or 9 digits' });
        return out;
    },
    'rendering-providers': (v) => {
        const out: InvalidField[] = [];
        (v.rendering?.providers || []).forEach((p: Values, i: number) => {
            const isFirst = i === 0;
            const editable = isFirst ? p?.sameAsBilling === 'no' : true;
            // same=yes 的 Provider 1 也有本行自己的 NPI 字段（预填/自填），NPI 校验一并生效；email 字段仅编辑态有
            const hasOwnNpi = editable || (isFirst && p?.sameAsBilling === 'yes');
            if (editable) {
                if (!isBlank(p?.email) && !validEmail(p.email))
                    out.push({ name: ['rendering', 'providers', i, 'email'], error: 'Enter a valid email' });
                // email 查重结果（ProviderEmailInput blur 校验写入的辅助字段）
                if (p?.emailStatus === 'duplicate')
                    out.push({
                        name: ['rendering', 'providers', i, 'email'],
                        error: 'This email has already been added',
                    });
                if (p?.emailStatus === 'unverifiable')
                    out.push({
                        name: ['rendering', 'providers', i, 'email'],
                        error: 'Provider email could not be verified',
                    });
            }
            if (hasOwnNpi) {
                if (!isBlank(p?.npi) && !validNpi(p.npi))
                    out.push({
                        name: ['rendering', 'providers', i, 'npi'],
                        error: 'NPI must be 10 digits',
                        inlineShown: true,
                    });
                // rendering provider 必须是 Individual（NPI-1）
                if (p?.npiType === 'Type 2')
                    out.push({
                        name: ['rendering', 'providers', i, 'npi'],
                        error: 'Rendering provider NPI must be Individual (NPI-1)',
                        inlineShown: true,
                    });
                if (p?.npiType === 'notfound')
                    out.push({
                        name: ['rendering', 'providers', i, 'npi'],
                        error: 'NPI not found in the NPI Registry',
                        inlineShown: true,
                    });
                // 同 billing：填了就必须查到（查询中 / 无查询结果都拦）
                else if (p?.npiChecking)
                    out.push({
                        name: ['rendering', 'providers', i, 'npi'],
                        error: 'NPI is being verified, please wait a moment',
                        inlineShown: true,
                    });
                else if (!isBlank(p?.npi) && validNpi(p.npi) && !p?.npiType)
                    out.push({
                        name: ['rendering', 'providers', i, 'npi'],
                        error: 'NPI could not be verified in the NPI Registry',
                    });
            }
            if (hasOwnNpi && taxonomyManualInvalid(p))
                out.push({
                    name: ['rendering', 'providers', i, 'taxonomy'],
                    error: 'Taxonomy code is not valid',
                    inlineShown: true,
                });
            if (p?.addressChoice === 'different' && !isBlank(p?.addr?.postalCode) && !validZip(p.addr.postalCode))
                out.push({
                    name: ['rendering', 'providers', i, 'addr', 'postalCode'],
                    error: 'ZIP must be 5 or 9 digits',
                });
        });
        return out;
    },
};

const invalidFields = (values: Values, key: BillingStepKey): InvalidField[] => INVALID[key]?.(values) || [];

interface RequiredField {
    label: string;
    name: NamePath | null;
}

const addressRequired = (ns: NamePath, prefix = ''): RequiredField[] => [
    { label: `${prefix}Address line 1`, name: [...ns, 'address1'] },
    { label: `${prefix}City`, name: [...ns, 'city'] },
    { label: `${prefix}State`, name: [...ns, 'state'] },
    { label: `${prefix}ZIP code`, name: [...ns, 'postalCode'] },
];

// 每个 section 的必填字段（含条件必填）；空必填只在 Review 页拦截
const REQUIRED: Record<BillingStepKey, (v: Values) => RequiredField[]> = {
    'billing-provider': () => [
        { label: 'Billing first name', name: ['billing', 'firstName'] },
        { label: 'Billing last name', name: ['billing', 'lastName'] },
        { label: 'Billing NPI', name: ['billing', 'npi'] },
        { label: 'Taxonomy', name: ['billing', 'taxonomy'] },
    ],
    'tax-info': () => [
        { label: 'Tax ID type', name: ['tax', 'taxIdType'] },
        { label: 'Tax ID number', name: ['tax', 'taxId'] },
    ],
    address: () => [...addressRequired(['address']), { label: 'Billing phone number', name: ['address', 'phone'] }],
    'service-location': (v) => {
        const base: RequiredField[] = [{ label: 'Same as billing address', name: ['location', 'sameAsBilling'] }];
        if (v.location?.sameAsBilling === 'no') {
            base.push({ label: 'Facility name', name: ['location', 'facilityName'] });
            base.push(...addressRequired(['location'], 'Service '));
        }
        return base;
    },
    'rendering-providers': (v) => {
        const out: RequiredField[] = [];
        // UI 始终至少渲染一行 provider，空列表按一行空 provider 校验
        const stored: Values[] = v.rendering?.providers || [];
        const providers: Values[] = stored.length ? stored : [{}];
        providers.forEach((p, i) => {
            const n = i + 1;
            const isFirst = i === 0;
            if (isFirst && isBlank(p?.sameAsBilling)) {
                out.push({
                    label: `Provider ${n} same as billing provider`,
                    name: ['rendering', 'providers', i, 'sameAsBilling'],
                });
                return;
            }
            const editable = isFirst ? p?.sameAsBilling === 'no' : true;
            if (editable) {
                out.push(
                    { label: `Provider ${n} first name`, name: ['rendering', 'providers', i, 'firstName'] },
                    { label: `Provider ${n} last name`, name: ['rendering', 'providers', i, 'lastName'] },
                    { label: `Provider ${n} email`, name: ['rendering', 'providers', i, 'email'] },
                    { label: `Provider ${n} NPI`, name: ['rendering', 'providers', i, 'npi'] },
                );
            } else {
                // same=yes：名字从 billing echo、无独立 email，但 NPI 是本行自己的字段——
                // billing 为 Individual 时已预填，为 Organization 时必须由用户填个人 NPI-1
                out.push({ label: `Provider ${n} NPI`, name: ['rendering', 'providers', i, 'npi'] });
            }
            out.push({ label: `Provider ${n} address`, name: ['rendering', 'providers', i, 'addressChoice'] });
            if (p?.addressChoice === 'different') {
                out.push(...addressRequired(['rendering', 'providers', i, 'addr'], `Provider ${n} `));
            }
        });
        return out;
    },
    'claim-scope': (v) => {
        const base: RequiredField[] = [
            { label: 'Patient scope', name: ['ehr', 'claimScope'] },
            { label: 'Start processing', name: ['ehr', 'claimStartMode'] },
        ];
        if (v.ehr?.claimStartMode === 'specific') {
            base.push({ label: 'Start date', name: ['ehr', 'claimStartDate'] });
        }
        return base;
    },
    // clearinghouse（era）实际可选（默认 switch），不做必填校验
    era: () => [],
    // payment：手动录卡态的完整性由保存时 Square tokenize 判定（卡号在 hosted iframe 里，读不到），
    // 不按缺失拦截；synced 态必须有卡。向导里本步是硬门禁，正常到不了没卡的 Review
    'payment-method': (v) =>
        v.payment?.useManualCard ? [] : [{ label: 'Payment method', name: ['payment', 'cardOnFile', 'last4'] }],
};
/* eslint-enable @typescript-eslint/naming-convention */

const missingFields = (values: Values, key: BillingStepKey) =>
    REQUIRED[key](values).filter((f) => isBlank(getByPath(values, f.name)));

// 顶部横幅用：缺失项 + 格式错误文案
export function getSectionErrors(values: Values, key: BillingStepKey): string[] {
    return [
        ...missingFields(values, key).map((f) => `${f.label} not provided`),
        ...invalidFields(values, key).map((f) => f.error),
    ];
}

export interface SectionFieldError {
    name: NamePath;
    errors: string[];
    // NpiRegistryBox 已在字段下方展示同义错误：setFields 标红时应跳过（fieldMarkableErrors），
    // 但保存/提交的拦截判断仍要把它算在内
    inlineShown?: boolean;
}

// 内联标红 + 保存拦截用：缺失/格式错误的字段 name + 报错文案
export function getSectionMissingFields(values: Values, key: BillingStepKey): SectionFieldError[] {
    return [
        ...missingFields(values, key)
            .filter((f) => f.name != null)
            .map((f) => ({ name: f.name as NamePath, errors: [`${f.label} is a required field`] })),
        ...invalidFields(values, key).map((f) => ({ name: f.name, errors: [f.error], inlineShown: f.inlineShown })),
    ];
}

// setFields 可用的标红列表：剔除 NpiRegistryBox 已内联展示的错误，避免上下两条重复红字
export const fieldMarkableErrors = (fields: SectionFieldError[]) =>
    fields.filter((f) => !f.inlineShown).map((f) => ({ name: f.name, errors: f.errors }));

export function getReviewErrors(values: Values): string[] {
    return REVIEW_SECTIONS.flatMap((sec) =>
        getSectionErrors(values, sec.key).map((e) => `${sec.title}: ${e.replace(/ not provided$/, '')}`),
    );
}

// ---------- 只读展示 ----------
const fmt = (v: any): React.ReactNode => {
    if (isBlank(v)) return null;
    if (React.isValidElement(v)) return v;
    if (Array.isArray(v)) return v.filter(Boolean).join(', ') || null;
    if (dayjs.isDayjs(v)) return v.format('YYYY-MM-DD');
    return String(v);
};

interface Item {
    label: string;
    value: any;
    optional?: boolean;
    // 值后面的琥珀色小标（原型 .ibbw-rev-flag，如 Pending / Differs from NPPES）
    flag?: React.ReactNode;
}

const ViewItem = ({ label, value, optional, flag }: Item) => {
    const text = fmt(value);
    return (
        <div className={s.reviewItem}>
            <div className={s.reviewItemLabel}>
                {label}
                {optional && <span className={s.reviewOptTag}>Optional</span>}
            </div>
            <div className={text ? s.reviewItemValue : `${s.reviewItemValue} ${s.reviewItemEmpty}`}>
                {text || 'Not provided'}
                {flag && <span className={s.reviewFlag}>{flag}</span>}
            </div>
        </div>
    );
};

// 只在向导 Review 生效的展示开关（settings 的同一张卡不带这些提示）
export interface SectionViewOpts {
    // 向导 Review（settings 不传）：原型里 rendering provider 的 Differs from NPPES 只在 settings 标
    review?: boolean;
    // 选了 Keep current clearinghouse 且 Ops 尚未放行：Routing 行标 Pending
    clearinghousePending?: boolean;
}

const NPPES_DIFF_TIP =
    'The provider picked a taxonomy code that is not on their NPPES record. Review before the first claim goes out.';

const ViewGrid = ({ items }: { items: Item[] }) => (
    <div className={s.reviewRow}>
        {items.map((it) => (
            <ViewItem key={it.label} {...it} />
        ))}
    </div>
);

// taxonomy code → "code · desc" 展示（options 来自 NPI 查询结果，缺失时退回 code）
const taxonomyLabel = (slot?: Values) => {
    if (!slot?.taxonomy) return '';
    const opt = (slot.taxonomyOptions || []).find((t: Values) => t.code === slot.taxonomy);
    return opt ? `${opt.code} · ${opt.desc}` : slot.taxonomy;
};

// NPI-1/NPI-2 展示文案
const npiTypeLabel = (t?: string) => {
    if (t === 'Type 2') return 'Organization (NPI-2)';
    if (t === 'Type 1') return 'Individual (NPI-1)';
    return '';
};

export function renderSectionView(key: BillingStepKey, values: Values, opts: SectionViewOpts = {}): React.ReactNode {
    if (key === 'billing-provider') {
        const b = values.billing || {};
        return (
            <ViewGrid
                items={[
                    { label: 'Billing first name', value: b.firstName },
                    { label: 'Billing last name', value: b.lastName },
                    { label: 'Organization name', value: b.organizationName, optional: true },
                    { label: 'Billing NPI', value: b.npi },
                    { label: 'NPI type', value: npiTypeLabel(b.npiType) },
                    {
                        label: 'Taxonomy',
                        // 格式不对的手填码按未填写展示（与提交拦截一致）
                        value: taxonomyManualInvalid(b) ? '' : taxonomyLabel(b),
                        flag:
                            !taxonomyManualInvalid(b) && taxonomyDiffersFromNppes(b) ? (
                                <span title={NPPES_DIFF_TIP}>Differs from NPPES</span>
                            ) : undefined,
                    },
                ]}
            />
        );
    }
    if (key === 'tax-info') {
        const t = values.tax || {};
        return (
            <ViewGrid
                items={[
                    // 存储为后端小写枚举，展示转大写（EIN / SSN）
                    { label: 'Tax ID type', value: t.taxIdType ? String(t.taxIdType).toUpperCase() : '' },
                    { label: 'Tax ID number', value: t.taxId },
                ]}
            />
        );
    }
    if (key === 'address') {
        const a = values.address || {};
        return (
            <ViewGrid
                items={[
                    { label: 'Address', value: addressSummary(a) },
                    { label: 'Phone', value: a.phone },
                    { label: 'Fax', value: a.fax, optional: true },
                    { label: 'Contact name', value: a.contactName, optional: true },
                    { label: 'Contact email', value: a.contactEmail, optional: true },
                ]}
            />
        );
    }
    if (key === 'service-location') {
        const l = values.location || {};
        if (l.sameAsBilling === 'no') {
            return (
                <ViewGrid
                    items={[
                        { label: 'Location', value: 'Different location' },
                        { label: 'Facility', value: l.facilityName },
                        { label: 'Service address', value: addressSummary(l) },
                    ]}
                />
            );
        }
        return (
            <ViewGrid
                items={[{ label: 'Location', value: l.sameAsBilling === 'yes' ? 'Same as billing address' : '' }]}
            />
        );
    }
    if (key === 'rendering-providers') {
        const b = values.billing || {};
        const stored: Values[] = values.rendering?.providers || [];
        const providers: Values[] = stored.length ? stored : [{}];
        return (
            <ViewGrid
                items={providers.map((p, i) => {
                    const same = i === 0 && p?.sameAsBilling === 'yes';
                    const nm = same
                        ? [b.firstName, b.lastName].filter(Boolean).join(' ')
                        : [p?.firstName, p?.lastName].filter(Boolean).join(' ');
                    // same 行的 NPI 也是本行自己的值（预填/自填），不再从 billing echo
                    const np = p?.npi;
                    // same-as-billing 的 provider 无独立 email（billing 步骤不采集）
                    const em = same ? undefined : p?.email;
                    return {
                        label: `Provider ${i + 1}`,
                        value: [nm, em, np].filter(Boolean).join(' · '),
                        flag:
                            !opts.review && taxonomyDiffersFromNppes(p) ? (
                                <span title={NPPES_DIFF_TIP}>Taxonomy differs from NPPES</span>
                            ) : undefined,
                    };
                })}
            />
        );
    }
    if (key === 'claim-scope') {
        const e = values.ehr || {};
        let start = '';
        if (e.claimStartMode === 'today') {
            start = e.claimTodayStartDate
                ? `Starting from ${formatTodayStartDate(e.claimTodayStartDate)}`
                : 'From today';
        }
        if (e.claimStartMode === 'specific') {
            start = e.claimStartDate ? `From ${fmt(e.claimStartDate)}` : 'From a specific date';
        }
        // 后端枚举值，向导 UI 不提供该选项；服务端回显时兜底展示
        if (e.claimStartMode === 'all_outstanding') start = 'All outstanding claims';
        const scopeLabels: Record<string, string> = { all: 'All patients', tagged: 'Only tagged patients' };
        return (
            <ViewGrid
                items={[
                    { label: 'Billing EHR(s)', value: (e.billingEhrs || []).join(', ') || 'Not connected yet' },
                    { label: 'Patient scope', value: scopeLabels[e.claimScope] || '' },
                    { label: 'Start processing', value: start },
                ]}
            />
        );
    }
    if (key === 'payment-method') {
        const c = values.payment?.cardOnFile || {};
        return (
            <ViewGrid
                items={[{ label: 'Payment method', value: c.last4 ? `${c.brand || 'Card'} ····${c.last4}` : '' }]}
            />
        );
    }
    // clearinghouse
    const choice = values.clearinghouse?.choice;
    const routingLabels: Record<string, string> = {
        switch: "Switch to Klarity's clearinghouse",
        keep: 'Keep current clearinghouse',
    };
    return (
        <ViewGrid
            items={[
                {
                    label: 'Routing',
                    value: routingLabels[choice] || '',
                    flag: opts.clearinghousePending && choice === 'keep' ? 'Pending' : undefined,
                },
            ]}
        />
    );
}
